import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, AlertTriangle, CalendarX } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatWib } from "@/lib/datetime";

const DAY = 86400000;
const SHOW_WITHIN_DAYS = 7;
const wibDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit" });
const pad = (n) => String(n).padStart(2, "0");

function countdown(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d > 0 ? `${d} hari ` : ""}${pad(h)} jam ${pad(m)} menit ${pad(s % 60)} detik`;
}

const TONES = {
  standard: "border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300",
  urgent: "border-destructive/50 bg-destructive/10 text-destructive",
  expired: "border-destructive bg-destructive/15 text-destructive",
};

/** Customer-only notice shown when their own access ends within 7 days (or has ended). */
export default function AccessNotice() {
  const { user } = useAuth();
  const isCustomer = !!user && user.role !== "admin";

  // Re-read the customer's OWN account data so an admin renewal shows up without reloading.
  const { data: me } = useQuery({
    queryKey: ["access-me"],
    queryFn: async () => (await api.get("/auth/me")).data,
    enabled: isCustomer,
    staleTime: 0,
    refetchInterval: 60 * 1000,
    refetchOnWindowFocus: true,
  });
  const expIso = me ? me.access_expires_at : user?.access_expires_at;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isCustomer || !expIso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isCustomer, expIso]);

  if (!isCustomer || !expIso) return null; // no expiration set = unlimited, no notice
  const exp = new Date(expIso);
  if (Number.isNaN(exp.getTime())) return null;
  const ms = exp.getTime() - now;
  if (ms > SHOW_WITHIN_DAYS * DAY) return null;

  const expiresLabel = formatWib(expIso);
  let level, Icon, title, body;
  if (ms <= 0) {
    level = "expired"; Icon = CalendarX;
    title = "Masa aktif akun Anda telah berakhir";
    body = `Akses berakhir pada ${expiresLabel}. Silakan hubungi admin untuk memperpanjang masa aktif. Data bisnis Anda tetap tersimpan dengan aman.`;
  } else if (wibDay.format(exp) === wibDay.format(new Date(now))) {
    level = "urgent"; Icon = AlertTriangle;
    title = "Masa aktif akun Anda berakhir HARI INI";
    body = `Akses berakhir hari ini pukul ${expiresLabel.split(", ")[1] || expiresLabel}. Segera hubungi admin agar Anda tetap bisa menggunakan aplikasi.`;
  } else if (Math.floor(ms / DAY) <= 3) {
    const d = Math.floor(ms / DAY);
    level = "urgent"; Icon = AlertTriangle;
    title = d >= 1 ? `Masa aktif akun Anda tinggal ${d} hari lagi` : "Masa aktif akun Anda tinggal kurang dari 1 hari";
    body = `Akses berakhir pada ${expiresLabel}. Segera hubungi admin untuk perpanjangan agar pekerjaan Anda tidak terputus.`;
  } else {
    level = "standard"; Icon = Clock;
    title = `Masa aktif akun Anda tinggal ${Math.floor(ms / DAY)} hari lagi`;
    body = `Akses berakhir pada ${expiresLabel}. Hubungi admin bila ingin memperpanjang.`;
  }

  return (
    <div role="alert" className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:p-5 ${TONES[level]}`}
      data-testid="access-notice" data-level={level}>
      <Icon className={`h-6 w-6 shrink-0 ${level === "standard" ? "" : "animate-pulse"}`} />
      <div className="min-w-0 flex-1">
        <p className="font-heading text-base font-bold sm:text-lg" data-testid="access-notice-title">{title}</p>
        <p className="text-sm opacity-90" data-testid="access-notice-body">{body}</p>
      </div>
      {ms > 0 && (
        <div className="rounded-xl bg-background/70 px-3 py-2 text-center sm:text-right">
          <p className="text-[11px] uppercase tracking-wide opacity-80">Sisa waktu</p>
          <p className="font-mono text-sm font-bold sm:text-base" data-testid="access-notice-countdown">{countdown(ms)}</p>
        </div>
      )}
    </div>
  );
}
